import { useCallback, useMemo, useState } from "react";

import type { SkillFileTreeController } from "./useSkillFileEditor";

import { buildSkillFileTree } from "./buildSkillFileTree";
import {
  movedPath,
  parentFolder,
  resolveSkillEntryName,
  type SkillEntryKind,
} from "./skillFilePaths";

/** A file or folder being named inline in the tree. */
type SkillTreeEntryDraft =
  | { mode: "new"; kind: SkillEntryKind; folder: string }
  | { mode: "rename"; kind: SkillEntryKind; path: string };

export type SkillTreeEntryEditing = ReturnType<typeof useSkillTreeEntryEditing>;

/**
 * New files and folders start unnamed in the tree with focus on their name;
 * a new file opens a blank editor meanwhile. Folders exist in S3 only through
 * their files, so a folder made here stays local until a file lands in it.
 */
export function useSkillTreeEntryEditing(editor: SkillFileTreeController) {
  const [entry, setEntry] = useState<SkillTreeEntryDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [emptyFolders, setEmptyFolders] = useState<string[]>([]);
  /** Where a new entry goes: the folder last clicked, or the open file's folder. */
  const [targetFolder, setTargetFolder] = useState("");

  const { paths } = editor;
  const tree = useMemo(() => buildSkillFileTree(paths, emptyFolders), [paths, emptyFolders]);

  const close = useCallback(() => {
    setEntry(null);
    setError(null);
  }, []);

  const startNew = useCallback(
    async (kind: SkillEntryKind) => {
      if (kind === "file" && !(await editor.clearSelection())) {
        return;
      }
      editor.expandFolder(targetFolder);
      setError(null);
      setEntry({ mode: "new", kind, folder: targetFolder });
    },
    [editor, targetFolder]
  );

  const startRename = useCallback((kind: SkillEntryKind, path: string) => {
    setError(null);
    setEntry({ mode: "rename", kind, path });
  }, []);

  const openFile = useCallback(
    async (path: string) => {
      if (!(await editor.openFile(path))) {
        return;
      }
      if (entry?.mode === "new" && entry.kind === "file") {
        close();
      }
      setTargetFolder(parentFolder(path));
    },
    [close, editor, entry]
  );

  const clickFolder = useCallback(
    (path: string) => {
      setTargetFolder(path);
      editor.toggleFolder(path);
    },
    [editor]
  );

  const renameEmptyFolders = useCallback((from: string, to: string) => {
    setEmptyFolders((current) => current.map((folder) => movedPath(folder, from, to) ?? folder));
  }, []);

  /** Apply the typed name. False keeps the input open (the error says why). */
  const commit = useCallback(
    async (name: string): Promise<boolean> => {
      if (!entry) {
        return true;
      }
      const folder = entry.mode === "new" ? entry.folder : parentFolder(entry.path);
      const resolved = resolveSkillEntryName({
        kind: entry.kind,
        folder,
        name,
        paths: editor.paths,
        folders: emptyFolders,
        currentPath: entry.mode === "rename" ? entry.path : undefined,
      });
      if ("error" in resolved) {
        setError(resolved.error);
        return false;
      }
      const { path } = resolved;

      if (entry.mode === "rename") {
        if (path !== entry.path && !(await editor.renamePath(entry.path, path))) {
          return false;
        }
        renameEmptyFolders(entry.path, path);
        setTargetFolder((current) => movedPath(current, entry.path, path) ?? current);
      } else if (entry.kind === "folder") {
        setEmptyFolders((current) => [...current, path]);
        editor.expandFolder(path);
        setTargetFolder(path);
      } else if (!(await editor.createFile(path, editor.draft))) {
        return false;
      }
      close();
      return true;
    },
    [close, editor, emptyFolders, entry, renameEmptyFolders]
  );

  return {
    tree,
    entry,
    error,
    clearError: () => setError(null),
    /** A new file is open in the editor but not named yet. */
    namingNewFile: entry?.mode === "new" && entry.kind === "file",
    startNew,
    startRename,
    cancel: close,
    commit,
    openFile,
    clickFolder,
  };
}
